import { debounce } from "@fraction/shared";
import * as React from "react";
import { forwardRef, useMemo } from "react";
import { cn } from "src/utilities/shadcnUtils";

type ButtonProps = React.PropsWithoutRef<
  React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
>;

export interface TouchableProps<
  Id extends object | string | undefined | Parameters<NonNullable<ButtonProps["onClick"]>> = any,
> extends Omit<ButtonProps, "onClick" | "id"> {
  isDefaultLayout?: boolean;
  id?: Id;
  onClick?: (id: any) => void;
}

export const TOUCHABLE_PADDING = {
  paddingLeft: 6,
  paddingRight: 6,
  paddingBottom: 3,
  paddingTop: 2,
};

const STYLES = {
  button:
    "bg-transparent shadow-[0px_0px_0px_transparent] border-0 border-solid border-transparent [text-shadow:0px_0px_0px_transparent] m-0 p-0 hover:shadow-[0px_0px_0px_transparent] hover:[text-shadow:0px_0px_0px_transparent] hover:cursor-pointer active:[color:unset] focus:outline-0",
  disabled: "hover:cursor-not-allowed",
  layout: "justify-center items-center flex pl-[6px] pr-[6px] pt-[2px] pb-[3px]",
  noAlign: "text-[inherit]",
};

const Touchable = forwardRef<HTMLButtonElement, TouchableProps>(
  ({ isDefaultLayout = true, onClick, disabled, id, className, ...props }, ref) => {
    const handleClick = useMemo(
      () => debounce((evt) => onClick?.(id || evt), 500, { leading: true }),
      [onClick, id]
    );

    return (
      <button
        ref={ref}
        type="button"
        {...props}
        className={cn(
          STYLES.button,
          "transition-opacity duration-200 active:opacity-60",
          isDefaultLayout && STYLES.layout,
          !isDefaultLayout && STYLES.noAlign,
          disabled && STYLES.disabled,
          className
        )}
        onClick={handleClick}
        disabled={disabled}
      />
    );
  }
);

export default React.memo(
  Touchable,
  (prev, next) =>
    prev.onClick === next.onClick &&
    prev.className === next.className &&
    prev.children === next.children &&
    prev.disabled === next.disabled &&
    prev.id === next.id
);
